"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Expand } from "lucide-react";
import { openImageLightbox } from "@/lib/image-lightbox-helper";

/** Below this the badge would cover the image it belongs to. */
const MIN_WIDTH = 96;
const MIN_HEIGHT = 64;

/**
 * `image-block-margin` comes from the image node's HTMLAttributes, so it marks
 * document images only — presence avatars, emoji and the upload placeholder
 * all render <img> inside .ProseMirror without it.
 */
const IMAGE_SELECTOR = "img.image-block-margin";

interface HoverTarget {
  src: string;
  alt: string;
  top: number;
  right: number;
}

/**
 * Floating "Open" affordance for editor images.
 *
 * Mounted once for the whole app rather than per editor, and deliberately not a
 * NodeView: wrapping the image would move `.ProseMirror-selectednode` off the
 * <img> that ImageResizer measures, breaking resize in every editor.
 */
export function EditorImageHoverButton() {
  const [target, setTarget] = useState<HoverTarget | null>(null);

  useEffect(() => {
    const onMouseOver = (event: MouseEvent) => {
      const node = event.target as Element | null;
      const image = node?.closest?.(IMAGE_SELECTOR) as HTMLImageElement | null;

      if (!image || !image.closest(".ProseMirror")) {
        // The badge sits over the image, so moving onto it must not hide it.
        if (node?.closest?.("[data-image-overlay]")) return;
        setTarget(null);
        return;
      }

      const rect = image.getBoundingClientRect();
      if (rect.width < MIN_WIDTH || rect.height < MIN_HEIGHT) {
        setTarget(null);
        return;
      }

      setTarget({
        src: image.currentSrc || image.src,
        alt: image.alt || "",
        top: rect.top + 8,
        // clientWidth, not innerWidth — the latter counts a classic scrollbar
        // that getBoundingClientRect does not, pushing the badge off the image.
        right: document.documentElement.clientWidth - rect.right + 8,
      });
    };

    const hide = () => setTarget(null);

    document.addEventListener("mouseover", onMouseOver);
    // Capture phase: the editor scrolls in its own container, not the window.
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      document.removeEventListener("mouseover", onMouseOver);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  if (!target) return null;

  return createPortal(
    <button
      type="button"
      // Keeps the block-selection plugin from clearing the image's selection.
      data-image-overlay="true"
      aria-label="Open image"
      title="Open image"
      onClick={() => {
        openImageLightbox([{ src: target.src, alt: target.alt }], 0);
        setTarget(null);
      }}
      style={{ top: target.top, right: target.right }}
      className="fixed z-[60] flex h-7 items-center gap-1.5 rounded-md bg-black/60 px-2 text-xs font-medium text-white shadow-sm ring-1 ring-white/15 backdrop-blur-sm transition-colors hover:bg-black/80"
    >
      <Expand className="h-3.5 w-3.5" />
      Open
    </button>,
    document.body,
  );
}

export default EditorImageHoverButton;
